import { oc } from "@orpc/contract";
import { z } from "zod";
import { Cents } from "./money";
import {
	GroupDiscountBody,
	ProductPricing,
	QuantityDiscountsBody,
	VariantQuantityDiscountsBody,
} from "./price";

/**
 * The catalog's admin API: products, their options and values, and their variants (CONTEXT.md).
 *
 * Built for the product editor (spec, "Beheer-UI"): one page that loads `get` and replaces it with
 * the `ProductEditor` every write answers, so the page never reloads or merges by hand. Errors come
 * back as `{ _tag, message }` with an HTTP status; the tags are named on each procedure. A write that
 * changes variants can also answer `ProductChanged` (409) when two writes raced: reload and retry.
 * Every write to an existing product (all but `create` and `restore`) answers `ProductArchived` (409)
 * while it is archived: read-only until it is restored (spec, A2). An invalid body or query answers 400.
 */

// ---------------------------------------------------------------------------------------------
// Fields

/** `standard`, or `bundle` (a product made of other variants: packages/contract/src/bundle.ts). */
export const ProductKind = z.enum(["standard", "bundle"]);
export type ProductKind = z.infer<typeof ProductKind>;

/** The product's URL name in the shop: lowercase letters and digits, single hyphens between. */
export const ProductSlug = z
	.string()
	.trim()
	.min(1)
	.max(100)
	.regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, digits and single hyphens");

/** A VAT rate as an integer percentage: `21`, `9`, `0`. */
export const VatRate = z.number().int().min(0).max(100);

/** A consumer price: incl VAT, in cents, never negative. */
export const ConsumerPrice = Cents.min(0);

/** A variant's stock-keeping unit: unique across all variants ever made, deleted ones included. */
export const Sku = z.string().trim().min(1).max(64).regex(/^\S+$/, "A SKU has no spaces");

/** True when `code` is 13 digits whose last is the EAN-13 check digit of the first twelve. */
export const isValidEan13 = (code: string): boolean => {
	if (!/^\d{13}$/.test(code)) return false;
	let sum = 0;
	for (let i = 0; i < 12; i++) sum += Number(code[i]) * (i % 2 === 0 ? 1 : 3);
	return (10 - (sum % 10)) % 10 === Number(code[12]);
};

/** An EAN-13 barcode, check digit verified. Unique like a SKU, deleted variants included. */
export const Ean = z
	.string()
	.trim()
	.regex(/^\d{13}$/, "An EAN has 13 digits")
	.refine(isValidEan13, "The EAN's check digit is wrong");

/** A variant's shipping weight in grams. */
export const WeightGrams = z.number().int().min(0).max(1_000_000);

/** What search engines show of a title and a description before they cut it off, roughly. */
export const SEO_ADVISED_LENGTH = { seoTitle: 60, seoDescription: 160 } as const;

/** Optional text: trimmed, and empty means none (`null`). */
const optionalText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.nullable()
		.transform((value) => value || null);

/** A product's SEO title; `null` (or empty) and the shop uses the name. */
export const SeoTitle = optionalText(200);
/** A product's SEO description; `null` (or empty) and the shop uses the start of the description. */
export const SeoDescription = optionalText(500);

/** `piecesPerPack`: how many pieces (doekjes, navullingen) one pack of a variant holds. */
export const PiecesPerPack = z.number().int().min(1).max(100_000);

/** What one piece is called, singular ("doek", "navulling"): "€0,35 per doek". Empty means none (`null`). */
export const PieceNoun = optionalText(30);

/** "Bijna op" from 1 up to this many pieces, summed over every live location; 0 never warns. */
export const LowStockThreshold = z.number().int().min(0).max(100_000);

/** The low-stock threshold a new product starts with. */
export const DEFAULT_LOW_STOCK_THRESHOLD = 5;

const ProductName = z.string().trim().min(1).max(200);
const OptionName = z.string().trim().min(1).max(100);
const Id = z.string().min(1);

// ---------------------------------------------------------------------------------------------
// The product editor

/** `fits`: "past op"; `notFor`: "niet voor" (CONTEXT.md, `fit`). */
export const Fit = z.enum(["fits", "notFor"]);
export type Fit = z.infer<typeof Fit>;

/** A variant's fit to one holder. */
export const VariantFit = z.object({ holderId: z.string(), fit: Fit });
export type VariantFit = z.infer<typeof VariantFit>;

/** A live option value: a chip in the editor. */
export const OptionValue = z.object({ id: z.string(), name: z.string() });
export type OptionValue = z.infer<typeof OptionValue>;

/** A live option with its live values, both in their set order. */
export const ProductOption = z.object({
	id: z.string(),
	name: z.string(),
	values: z.array(OptionValue),
});
export type ProductOption = z.infer<typeof ProductOption>;

/** The image types a photo may be: checked on the file's bytes, not on its name. */
export const PhotoContentType = z.enum(["image/jpeg", "image/png", "image/webp", "image/avif"]);
export type PhotoContentType = z.infer<typeof PhotoContentType>;

/** The largest photo the admin may upload: 10 MB. */
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

/**
 * A product photo. Only the original is stored; `sizes` are made from it on request by Cloudflare
 * Image Transformations, as AVIF or WebP when the browser takes them. Where transformations are off
 * (Previews, local development) every size is the original's URL. URLs may be root-relative.
 */
export const ProductPhoto = z.object({
	id: z.string(),
	contentType: PhotoContentType,
	/** The original's size in bytes. */
	sizeBytes: z.number().int(),
	/** The original, as uploaded. */
	url: z.string(),
	/** Smallest first: at least 400, 800 and 1600 pixels wide (never wider than the original). */
	sizes: z.array(z.object({ width: z.number().int(), url: z.string() })),
	/** What the photo shows, as typed (`PhotoAltText`); `""` when not written yet. */
	altText: z.string(),
	createdAt: z.iso.datetime(),
});
export type ProductPhoto = z.infer<typeof ProductPhoto>;

/**
 * One of a variant's option values. `archived` when the value (or its option) was removed while
 * variants held it: such a variant is inactive and shows the old value's name.
 */
export const VariantOptionValue = z.object({
	optionId: z.string(),
	optionValueId: z.string(),
	name: z.string(),
	archived: z.boolean(),
});
export type VariantOptionValue = z.infer<typeof VariantOptionValue>;

/** A row of the variant table. Deleted variants never appear. */
export const Variant = z.object({
	id: z.string(),
	/** The value names in option order (`XXL / Blauw`); the product's name for a variant without values. */
	name: z.string(),
	sku: z.string(),
	ean: z.string().nullable(),
	weightGrams: z.number().int().nullable(),
	/** Incl VAT, in cents. */
	consumerPrice: Cents,
	/**
	 * How many pieces one pack holds (`PiecesPerPack`); `null` without pieces. A bundle's variant
	 * left `null` counts its components' pieces in the shop.
	 */
	piecesPerPack: z.number().int().nullable(),
	/** What one piece is called, singular (`PieceNoun`); `null` when not said. */
	pieceNoun: z.string().nullable(),
	active: z.boolean(),
	optionValues: z.array(VariantOptionValue),
	/**
	 * The attribute values set on this variant itself (`setVariantAttributeValues`). For an
	 * attribute it has a value of, it replaces the product's values in the shop.
	 */
	attributeValueIds: z.array(z.string()),
	/** Its fit per holder ("past op" / "niet voor"); a holder not listed is unknown. In holder order. */
	fits: z.array(VariantFit),
});
export type Variant = z.infer<typeof Variant>;

/** Everything the product editor shows: the product, its options and its variants, in order. */
export const ProductEditor = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	description: z.string(),
	vatRate: z.number().int(),
	active: z.boolean(),
	kind: ProductKind,
	/** "Zelfde prijs voor alle varianten": one price field instead of a price column. */
	samePriceForAllVariants: z.boolean(),
	/** The one price all variants share while `samePriceForAllVariants` is on; `null` when off. */
	consumerPrice: Cents.nullable(),
	/** As typed; `null` when empty. The shop falls back to the name. */
	seoTitle: z.string().nullable(),
	/** As typed; `null` when empty. The shop falls back to the start of the description. */
	seoDescription: z.string().nullable(),
	/** The Voorraad page's "Bijna op": 1 up to this many pieces (`LowStockThreshold`). */
	lowStockThreshold: z.number().int(),
	/** When it was archived (`removeProduct`); `null`: not archived. An archived product is never for sale. */
	archivedAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
	options: z.array(ProductOption),
	/** Ordered as the grid: by option order, then by value order within each option. */
	variants: z.array(Variant),
	/** In their set order; the first is the main photo. */
	photos: z.array(ProductPhoto),
	/** The groups the product is in (`setCategories`), in the groups' order. */
	categoryIds: z.array(z.string()),
	/** The attribute values that hold for the product and so for all its variants (`setAttributeValues`). */
	attributeValueIds: z.array(z.string()),
	/** Customer group discounts and quantity discounts (`ProductPricing`, packages/contract/src/price.ts). */
	pricing: ProductPricing,
});
export type ProductEditor = z.infer<typeof ProductEditor>;

/**
 * What a removal answers: the editor, and the variants the removal switched off, so an undo notice
 * can switch exactly those back on (`restoreOptionValue`).
 */
export const ProductRemoval = z.object({
	product: ProductEditor,
	deactivatedVariantIds: z.array(z.string()),
});
export type ProductRemoval = z.infer<typeof ProductRemoval>;

/** A variant that lost its value for a removed option, and the value it held. */
export const DetachedVariant = z.object({ variantId: z.string(), optionValueId: z.string() });
export type DetachedVariant = z.infer<typeof DetachedVariant>;

/**
 * What removing an option answers: a removal, plus the variants that live on without the option and
 * the value each held, so the undo notice can give them their value back (`restoreOption`).
 */
export const OptionRemoval = ProductRemoval.extend({ detachedVariants: z.array(DetachedVariant) });
export type OptionRemoval = z.infer<typeof OptionRemoval>;

// ---------------------------------------------------------------------------------------------
// The product list

/** `all`, `active` and `inactive` leave archived products out; `archived` shows only those. */
export const ProductStatusFilter = z.enum(["all", "active", "inactive", "archived"]);
export type ProductStatusFilter = z.infer<typeof ProductStatusFilter>;

/** The list's query string, as the admin keeps it in its own URL. */
export const ProductListQuery = z.object({
	/** Part of the product's name or of one of its variants' SKUs, any case. */
	search: z.string().trim().max(200).optional(),
	status: ProductStatusFilter.default("all"),
	/** 1-based. */
	page: z.coerce.number().int().min(1).default(1),
	pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type ProductListQuery = z.infer<typeof ProductListQuery>;

export const ProductListItem = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	kind: ProductKind,
	active: z.boolean(),
	variantCount: z.number().int(),
	/** The SKU when the product has exactly one variant. */
	sku: z.string().nullable(),
	/** The lowest and highest consumer price over its variants; equal for one variant. */
	minConsumerPrice: Cents.nullable(),
	maxConsumerPrice: Cents.nullable(),
	/** `null` unless the list shows archived products. */
	archivedAt: z.iso.datetime().nullable(),
	updatedAt: z.iso.datetime(),
});
export type ProductListItem = z.infer<typeof ProductListItem>;

/** One page of products, sorted by name, with the number of products that match. */
export const ProductListPage = z.object({
	items: z.array(ProductListItem),
	total: z.number().int(),
	page: z.number().int(),
	pageSize: z.number().int(),
});
export type ProductListPage = z.infer<typeof ProductListPage>;

// ---------------------------------------------------------------------------------------------
// Deleting a product (spec, A2)

/**
 * What deleting the product would do, and why. Without history it is `delete`d, with everything of
 * its own (variants, photos, prices, its opening stock). With history it is `archive`d: the counts
 * say which history (or the discount codes that name it). Orders count the orders with a line of it, or holding one of its variants as a
 * bundle's component; invoices belong to orders, so they are in that count.
 */
export const ProductDeletionPlan = z.object({
	outcome: z.enum(["delete", "archive"]),
	orderCount: z.number().int(),
	/** Stock movements other than each variant's opening receipt. */
	stockMovementCount: z.number().int(),
	/** The bundles one of its variants is a component of, by name. */
	bundles: z.array(z.object({ id: z.string(), name: z.string() })),
	/**
	 * The discount codes whose scope names it. Not history, but deleting it would leave a code
	 * scoped to it alone valid for every product, so it is archived instead.
	 */
	discountCodes: z.array(z.object({ id: z.string(), code: z.string() })),
});
export type ProductDeletionPlan = z.infer<typeof ProductDeletionPlan>;

/** What deleting answered: gone for good, or archived (with the editor, which shows it archived). */
export const ProductDeletion = z.discriminatedUnion("outcome", [
	z.object({ outcome: z.literal("deleted") }),
	z.object({ outcome: z.literal("archived"), product: ProductEditor }),
]);
export type ProductDeletion = z.infer<typeof ProductDeletion>;

// ---------------------------------------------------------------------------------------------
// Inputs. Path parameters (`productId`, ...) are separate from the bodies, so the API validates a
// body by itself; the procedures below take both as one object, as the oRPC client sends them.

/**
 * A new product. It starts with one variant, at `consumerPrice`, with a proposed SKU. The kind is
 * chosen here and never changes: a `bundle` gets its components in the bundle area.
 */
export const CreateProductInput = z.object({
	kind: ProductKind.default("standard"),
	name: ProductName,
	slug: ProductSlug,
	description: z.string().max(20_000).default(""),
	vatRate: VatRate.default(21),
	active: z.boolean().default(true),
	consumerPrice: ConsumerPrice,
});
export type CreateProductInput = z.infer<typeof CreateProductInput>;

/**
 * Changes to a product; fields left out stay as they are.
 *
 * - `consumerPrice` sets every variant's price at once, and needs `samePriceForAllVariants` on.
 * - Turning `samePriceForAllVariants` on without a `consumerPrice` gives every variant the price of
 *   the first variant.
 */
export const UpdateProductBody = z.object({
	name: ProductName.optional(),
	slug: ProductSlug.optional(),
	description: z.string().max(20_000).optional(),
	vatRate: VatRate.optional(),
	active: z.boolean().optional(),
	samePriceForAllVariants: z.boolean().optional(),
	consumerPrice: ConsumerPrice.optional(),
	/** `null` or empty clears it. */
	seoTitle: SeoTitle.optional(),
	/** `null` or empty clears it. */
	seoDescription: SeoDescription.optional(),
	lowStockThreshold: LowStockThreshold.optional(),
});
export type UpdateProductBody = z.infer<typeof UpdateProductBody>;

export const NameBody = z.object({ name: OptionName });
export type NameBody = z.infer<typeof NameBody>;

/** The full list of live option ids (or of one option's live value ids), in the new order. */
export const OptionOrderBody = z.object({ optionIds: z.array(Id).min(1) });
export type OptionOrderBody = z.infer<typeof OptionOrderBody>;
export const OptionValueOrderBody = z.object({ valueIds: z.array(Id).min(1) });
export type OptionValueOrderBody = z.infer<typeof OptionValueOrderBody>;

/** Undoes a removal: the variants it switched off (`ProductRemoval.deactivatedVariantIds`). */
export const RestoreOptionValueBody = z.object({ reactivateVariantIds: z.array(Id).default([]) });
export type RestoreOptionValueBody = z.infer<typeof RestoreOptionValueBody>;

/**
 * Undoes `removeOption`: what its `OptionRemoval` listed. The variants that lived on without the
 * option get their value back; the variants it switched off are switched on again.
 */
export const RestoreOptionBody = z.object({
	reactivateVariantIds: z.array(Id).default([]),
	reattach: z.array(z.object({ variantId: Id, optionValueId: Id })).default([]),
});
export type RestoreOptionBody = z.infer<typeof RestoreOptionBody>;

/** Two live values of one option; see `swapOptionValues`. */
export const SwapOptionValuesBody = z.object({ valueId: Id, otherValueId: Id });
export type SwapOptionValuesBody = z.infer<typeof SwapOptionValuesBody>;

/**
 * Changes to one variant; fields left out stay as they are. `ean`, `weightGrams`, `piecesPerPack`
 * and `pieceNoun` take `null` to clear. `consumerPrice` is refused while the product has
 * `samePriceForAllVariants` on.
 */
export const UpdateVariantBody = z.object({
	sku: Sku.optional(),
	ean: Ean.nullable().optional(),
	weightGrams: WeightGrams.nullable().optional(),
	piecesPerPack: PiecesPerPack.nullable().optional(),
	pieceNoun: PieceNoun.optional(),
	active: z.boolean().optional(),
	consumerPrice: ConsumerPrice.optional(),
});
export type UpdateVariantBody = z.infer<typeof UpdateVariantBody>;

const ProductPath = z.object({ productId: Id });
const OptionPath = ProductPath.extend({ optionId: Id });
const OptionValuePath = OptionPath.extend({ valueId: Id });
const VariantPath = ProductPath.extend({ variantId: Id });
const PhotoPath = ProductPath.extend({ photoId: Id });

/** A photo's alt text: what it shows, for screen readers and search engines. Trimmed; empty clears it. */
export const PhotoAltText = z.string().trim().max(250);

/** Changes to one photo. */
export const UpdatePhotoBody = z.object({ altText: PhotoAltText });
export type UpdatePhotoBody = z.infer<typeof UpdatePhotoBody>;

/** The full list of the product's photo ids, in the new order. */
export const PhotoOrderBody = z.object({ photoIds: z.array(Id).min(1) });
export type PhotoOrderBody = z.infer<typeof PhotoOrderBody>;
const GroupDiscountPath = ProductPath.extend({ customerGroupId: Id });

const distinctIds = (what: string) =>
	z
		.array(Id)
		.max(200)
		.refine((ids) => new Set(ids).size === ids.length, `each ${what} once`);

/** Every group the product is in, replacing the ones it was in; empty takes it out of all. */
export const ProductCategoriesBody = z.object({ categoryIds: distinctIds("group") });
export type ProductCategoriesBody = z.infer<typeof ProductCategoriesBody>;

/** Every attribute value of the product (or variant), replacing what it had; empty clears them. */
export const AttributeValuesBody = z.object({ attributeValueIds: distinctIds("value") });
export type AttributeValuesBody = z.infer<typeof AttributeValuesBody>;

/** The variant's fit per holder, replacing what it had; a holder left out becomes unknown. */
export const VariantFitsBody = z.object({
	fits: z
		.array(VariantFit)
		.max(200)
		.refine((fits) => new Set(fits.map((fit) => fit.holderId)).size === fits.length, "each holder once"),
});
export type VariantFitsBody = z.infer<typeof VariantFitsBody>;

// ---------------------------------------------------------------------------------------------
// Procedures (admin only: every one needs a signed-in employee)

export const ProductContract = {
	/** The product list, searchable by name and SKU, filtered on active. */
	list: oc.route({ method: "GET", path: "/admin/products" }).input(ProductListQuery).output(ProductListPage),
	/** Errors: `SlugTaken` (409). */
	create: oc
		.route({ method: "POST", path: "/admin/products" })
		.input(CreateProductInput)
		.output(ProductEditor),
	/** Errors: `ProductNotFound` (404). */
	get: oc
		.route({ method: "GET", path: "/admin/products/{productId}" })
		.input(ProductPath)
		.output(ProductEditor),
	/** What `remove` would do now, and why. Errors: `ProductNotFound` (404). */
	deletionPlan: oc
		.route({ method: "GET", path: "/admin/products/{productId}/deletion" })
		.input(ProductPath)
		.output(ProductDeletionPlan),
	/**
	 * Deletes the product when it has no history, else archives it (`ProductDeletionPlan`), decided
	 * again at this moment. Errors: `ProductNotFound` (404), `ProductChanged` (409): history appeared
	 * between deciding and deleting; ask again.
	 */
	remove: oc
		.route({ method: "DELETE", path: "/admin/products/{productId}" })
		.input(ProductPath)
		.output(ProductDeletion),
	/**
	 * Brings an archived product back as it was (active or not); a product that is not archived is
	 * left as it is. Errors: `ProductNotFound` (404).
	 */
	restore: oc
		.route({ method: "POST", path: "/admin/products/{productId}/restore" })
		.input(ProductPath)
		.output(ProductEditor),
	/** Errors: `ProductNotFound` (404), `SlugTaken` (409), `SamePriceIsOff` (409). */
	update: oc
		.route({ method: "PATCH", path: "/admin/products/{productId}" })
		.input(ProductPath.extend(UpdateProductBody.shape))
		.output(ProductEditor),

	/**
	 * A new option, without values: no variant changes until its first value. Errors:
	 * `ProductNotFound` (404), `OptionNameTaken` (409), `BundleWithoutOptions` (409): a bundle has
	 * one variant, so no options.
	 */
	addOption: oc
		.route({ method: "POST", path: "/admin/products/{productId}/options" })
		.input(ProductPath.extend(NameBody.shape))
		.output(ProductEditor),
	/** Errors: `OptionNotFound` (404), `OptionNameTaken` (409). */
	renameOption: oc
		.route({ method: "PATCH", path: "/admin/products/{productId}/options/{optionId}" })
		.input(OptionPath.extend(NameBody.shape))
		.output(ProductEditor),
	/**
	 * Removes an option and drops it from the variants. Variants that differed only in this option
	 * collapse into one: the one with the option's first value keeps living without it, the others
	 * are switched off (and listed). The option and its values are archived, never deleted, so the
	 * removal can be undone. Errors: `OptionNotFound` (404).
	 */
	removeOption: oc
		.route({ method: "DELETE", path: "/admin/products/{productId}/options/{optionId}" })
		.input(OptionPath)
		.output(OptionRemoval),
	/**
	 * Undoes `removeOption`: the option is live again at its place, with the values the removal
	 * archived; the listed variants get their value back or are switched on again; and combinations
	 * made while it was gone get their variants, as `addOptionValue` does. Errors: `OptionNotFound`
	 * (404): no removed option with this id; `OptionNameTaken` (409): a live option took its name;
	 * `CombinationTaken` (409); `TooManyVariants` (409).
	 */
	restoreOption: oc
		.route({ method: "POST", path: "/admin/products/{productId}/options/{optionId}/restore" })
		.input(OptionPath.extend(RestoreOptionBody.shape))
		.output(ProductEditor),
	/** Errors: `ProductNotFound` (404), `InvalidOrder` (400): not exactly the live options. */
	reorderOptions: oc
		.route({ method: "PUT", path: "/admin/products/{productId}/options/order" })
		.input(ProductPath.extend(OptionOrderBody.shape))
		.output(ProductEditor),

	/**
	 * Adds a value and makes the variants for it at once:
	 *
	 * - the option's first value is given to the existing variants (change it with
	 *   `swapOptionValues`);
	 * - every further value gets a new variant per new combination, with the consumer price of a
	 *   sibling (the variant that differs only in this option; the shared price while
	 *   `samePriceForAllVariants` is on) and a proposed SKU. Existing variants stay as they are.
	 *
	 * Errors: `OptionNotFound` (404), `OptionValueNameTaken` (409), `TooManyVariants` (409).
	 */
	addOptionValue: oc
		.route({ method: "POST", path: "/admin/products/{productId}/options/{optionId}/values" })
		.input(OptionPath.extend(NameBody.shape))
		.output(ProductEditor),
	/** Errors: `OptionValueNotFound` (404), `OptionValueNameTaken` (409). */
	renameOptionValue: oc
		.route({ method: "PATCH", path: "/admin/products/{productId}/options/{optionId}/values/{valueId}" })
		.input(OptionValuePath.extend(NameBody.shape))
		.output(ProductEditor),
	/**
	 * Removes a value: it is archived, and the variants holding it are switched off (and listed, for
	 * the undo notice). Errors: `OptionValueNotFound` (404).
	 */
	removeOptionValue: oc
		.route({ method: "DELETE", path: "/admin/products/{productId}/options/{optionId}/values/{valueId}" })
		.input(OptionValuePath)
		.output(ProductRemoval),
	/**
	 * Undoes `removeOptionValue` for an archived value: it is live again, the listed variants are
	 * switched back on, and missing combinations with it are made as `addOptionValue` does. Errors:
	 * `OptionValueNotFound` (404), `OptionValueNameTaken` (409), `TooManyVariants` (409).
	 */
	restoreOptionValue: oc
		.route({
			method: "POST",
			path: "/admin/products/{productId}/options/{optionId}/values/{valueId}/restore",
		})
		.input(OptionValuePath.extend(RestoreOptionValueBody.shape))
		.output(ProductEditor),
	/** Errors: `OptionNotFound` (404), `InvalidOrder` (400): not exactly the option's live values. */
	reorderOptionValues: oc
		.route({ method: "PUT", path: "/admin/products/{productId}/options/{optionId}/values/order" })
		.input(OptionPath.extend(OptionValueOrderBody.shape))
		.output(ProductEditor),
	/**
	 * The variants that hold `valueId` get `otherValueId` and the other way round; each keeps its own
	 * SKU, EAN, price and stock. The editor's "existing variants get [value]" choice after adding an
	 * option: swap the value they got with the one chosen. Errors: `OptionValueNotFound` (404).
	 */
	swapOptionValues: oc
		.route({ method: "POST", path: "/admin/products/{productId}/options/{optionId}/swap-values" })
		.input(OptionPath.extend(SwapOptionValuesBody.shape))
		.output(ProductEditor),

	/**
	 * Errors: `VariantNotFound` (404), `SkuTaken` (409), `EanTaken` (409), `SharedPrice` (409): the
	 * price is set on the product while `samePriceForAllVariants` is on.
	 */
	updateVariant: oc
		.route({ method: "PATCH", path: "/admin/products/{productId}/variants/{variantId}" })
		.input(VariantPath.extend(UpdateVariantBody.shape))
		.output(ProductEditor),
	/**
	 * Silent delete: the variant disappears from the admin, the shop and every lookup, but stays in
	 * the database (`deletedAt`), so orders and invoices keep it and its SKU and EAN stay taken.
	 * Errors: `VariantNotFound` (404), `LastVariant` (409): a product keeps at least one variant.
	 */
	deleteVariant: oc
		.route({ method: "DELETE", path: "/admin/products/{productId}/variants/{variantId}" })
		.input(VariantPath)
		.output(ProductEditor),
	/**
	 * Undoes `deleteVariant`: the variant is back as it was (with the shared price while
	 * `samePriceForAllVariants` is on). Errors: `VariantNotFound` (404): no deleted variant with this
	 * id; `CombinationTaken` (409): a live variant stands for its combination now; `TooManyVariants`
	 * (409).
	 */
	restoreVariant: oc
		.route({ method: "POST", path: "/admin/products/{productId}/variants/{variantId}/restore" })
		.input(VariantPath)
		.output(ProductEditor),

	/**
	 * Adds a photo at the end. The body is the file itself (not a form): send a `File` as `body`, and
	 * the client sends it with its own `Content-Type`. JPEG, PNG, WebP or AVIF, at most
	 * `MAX_PHOTO_BYTES`. Errors: `ProductNotFound` (404), `NotAnImage` (415; also any other
	 * `Content-Type`), 413 when the file is too large.
	 */
	uploadPhoto: oc
		.route({ method: "POST", path: "/admin/products/{productId}/photos", inputStructure: "detailed" })
		.input(z.object({ params: ProductPath, body: z.file() }))
		.output(ProductEditor),
	/** Errors: `ProductNotFound` (404), `InvalidOrder` (400): not exactly the product's photos. */
	reorderPhotos: oc
		.route({ method: "PUT", path: "/admin/products/{productId}/photos/order" })
		.input(ProductPath.extend(PhotoOrderBody.shape))
		.output(ProductEditor),
	/** Sets the photo's alt text (#161). Errors: `PhotoNotFound` (404). */
	updatePhoto: oc
		.route({ method: "PATCH", path: "/admin/products/{productId}/photos/{photoId}" })
		.input(PhotoPath.extend(UpdatePhotoBody.shape))
		.output(ProductEditor),
	/** Deletes the photo and its stored file. Errors: `PhotoNotFound` (404). */
	deletePhoto: oc
		.route({ method: "DELETE", path: "/admin/products/{productId}/photos/{photoId}" })
		.input(PhotoPath)
		.output(ProductEditor),

	/**
	 * "Afwijken voor dit product": the product's own discount for one business group, for all its
	 * variants (there is no per-variant group discount); `percent: null` follows the group's default
	 * again. Errors: `ProductNotFound` (404), `CustomerGroupNotFound` (404), `ConsumerGroupFixed`
	 * (409): Consument is always 0%.
	 */
	setGroupDiscount: oc
		.route({ method: "PUT", path: "/admin/products/{productId}/group-discounts/{customerGroupId}" })
		.input(GroupDiscountPath.extend(GroupDiscountBody.shape))
		.output(ProductEditor),
	/**
	 * "Staffelkorting": the shop's list, the product's own list, or none, for all variants and every
	 * customer group. Errors: `ProductNotFound` (404).
	 */
	setQuantityDiscounts: oc
		.route({ method: "PUT", path: "/admin/products/{productId}/quantity-discounts" })
		.input(ProductPath.and(QuantityDiscountsBody))
		.output(ProductEditor),
	/** Puts the product in exactly these groups. Errors: `ProductNotFound` (404), `UnknownCategory` (404). */
	setCategories: oc
		.route({ method: "PUT", path: "/admin/products/{productId}/categories" })
		.input(ProductPath.extend(ProductCategoriesBody.shape))
		.output(ProductEditor),
	/**
	 * The product's own attribute values, for all its variants. At most one value of the size
	 * attribute. Errors: `ProductNotFound` (404), `UnknownAttributeValue` (404), `TwoSizes` (409).
	 */
	setAttributeValues: oc
		.route({ method: "PUT", path: "/admin/products/{productId}/attribute-values" })
		.input(ProductPath.extend(AttributeValuesBody.shape))
		.output(ProductEditor),
	/**
	 * A variant's own attribute values. At most one value of the size attribute. Errors:
	 * `ProductNotFound` (404), `VariantNotFound` (404), `UnknownAttributeValue` (404), `TwoSizes` (409).
	 */
	setVariantAttributeValues: oc
		.route({ method: "PUT", path: "/admin/products/{productId}/variants/{variantId}/attribute-values" })
		.input(VariantPath.extend(AttributeValuesBody.shape))
		.output(ProductEditor),
	/**
	 * A variant's fit per holder: "past op" or "niet voor"; a holder left out is unknown. Errors:
	 * `ProductNotFound` (404), `VariantNotFound` (404), `UnknownHolder` (404).
	 */
	setVariantFits: oc
		.route({ method: "PUT", path: "/admin/products/{productId}/variants/{variantId}/fits" })
		.input(VariantPath.extend(VariantFitsBody.shape))
		.output(ProductEditor),
	/**
	 * The exception, from the variant's side panel: a variant's own list, which replaces the product's
	 * (whatever its mode) for that variant; `null` follows the product again. Errors: `ProductNotFound`
	 * (404), `VariantNotFound` (404).
	 */
	setVariantQuantityDiscounts: oc
		.route({ method: "PUT", path: "/admin/products/{productId}/variants/{variantId}/quantity-discounts" })
		.input(VariantPath.extend(VariantQuantityDiscountsBody.shape))
		.output(ProductEditor),
};
